import {
  DomainEntity,
  DomainEntityWithRights,
  EntityWithActions,
  Identifier,
} from "@/types/domain";
import { EventDates, EventDatesAlt, EventStage } from "@features/events/types/event";
import { ActivityScope, ActivityTypeValue } from "@features/proposals/types/activity";
import { ActivityMemberRoleType, ActivityMemberScope } from "@features/proposals/types/member";
import {
  ProposalAggWorkflowOutputView,
  ProposalWorkflowStatus,
} from "@features/proposals/types/proposal";
import {
  ProposalAssignmentEntity,
  ProposalAssignmentKind,
  ProposalAssignmentOutputView,
  ProposalAssignmentStatus,
} from "@features/events-program/types/proposal-assignment";
import {
  ProposalVoteEntity,
  ProposalVoteOutputView,
} from "@features/events-program/types/proposal-vote";
import { ProgramProposalEntityRightsTypes } from "@features/events-program/types/rights";

export interface ContentDataPreview<TData = unknown> {
  readonly data?: TData;
}

export interface ProgramAssignmentAggregatesQueryParams {
  readonly kinds?: ProposalAssignmentKind[];
  readonly statuses?: ProposalAssignmentStatus[];
  readonly event_ids?: number[];
  readonly page_num?: number;
  readonly page_size?: number;
}

export const PROGRAM_ASSIGNMENT_AGGREGATES_DEFAULT_QUERY: Required<
  Pick<ProgramAssignmentAggregatesQueryParams, "kinds" | "statuses" | "page_num" | "page_size">
> = {
  kinds: ["CURATOR", "REVIEWER"],
  statuses: ["ACTIVE"],
  page_num: 0,
  page_size: 20,
};

export const buildProgramAssignmentAggregatesQueryParams = (
  query?: ProgramAssignmentAggregatesQueryParams,
): ProgramAssignmentAggregatesQueryParams => ({
  ...PROGRAM_ASSIGNMENT_AGGREGATES_DEFAULT_QUERY,
  ...query,
});

export interface EventPreview {
  readonly content?: ContentDataPreview;
  readonly dates?: EventDates;
  readonly datesAlt?: EventDatesAlt;
  readonly id?: number;
  readonly projectId?: string;
  readonly slug?: string;
  readonly stage?: EventStage;
  readonly workspaceId?: string;
}

export interface EventProfilePreview {
  readonly biography?: {
    readonly content?: ContentDataPreview;
  };
}

export interface ActivityMemberPreview {
  readonly activityId?: number;
  readonly contributorId?: number;
  readonly eventProfile?: EventProfilePreview;
  readonly id?: number;
  readonly role?: ActivityMemberRoleType;
  readonly scope?: ActivityMemberScope;
}

export interface ActivityPreview {
  readonly content?: ContentDataPreview;
  readonly eventId?: number;
  readonly id?: number;
  readonly scope?: ActivityScope;
  readonly typeValue?: ActivityTypeValue;
}

export interface ProposalPreview extends EntityWithActions {
  readonly activity?: ActivityPreview;
  readonly eventId?: number;
  readonly id?: Identifier;
  readonly members?: ActivityMemberPreview[];
  readonly rating?: number;
  readonly vote?: ProposalVoteOutputView;
  readonly voteCount?: number;
  readonly workflow?: ProposalAggWorkflowOutputView & {
    readonly status?: ProposalWorkflowStatus | string;
  };
}

export interface ProposalPreviewEntity
  extends
    Omit<DomainEntity<ProposalPreview>, "activity" | "members" | "vote">,
    DomainEntityWithRights<ProgramProposalEntityRightsTypes> {
  readonly activity?: ActivityPreview;
  readonly members?: ActivityMemberPreview[];
  readonly vote?: ProposalVoteEntity;
}

export interface ProposalAssignmentAggregateOutputView extends EntityWithActions {
  readonly assignment?: ProposalAssignmentOutputView;
  readonly createdAt?: string;
  readonly event?: EventPreview;
  readonly id?: number;
  readonly proposal?: ProposalPreview;
  readonly updatedAt?: string;
}

export interface ProposalAssignmentAggregateEntity extends Omit<
  DomainEntity<ProposalAssignmentAggregateOutputView>,
  "assignment" | "proposal" | "event"
> {
  readonly assignment?: ProposalAssignmentEntity;
  readonly event?: EventPreview;
  readonly proposal?: ProposalPreviewEntity;
}
